import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Gift, Copy, WhatsappLogo, XLogo, Envelope, Users, Crown, CheckCircle, ShareNetwork,
} from "@phosphor-icons/react";

export default function Referrals() {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [redeemCode, setRedeemCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);

  const load = async () => {
    const [s, h] = await Promise.all([
      api.get("/referrals/me"),
      api.get("/referrals/history"),
    ]);
    setStatus(s.data);
    setHistory(h.data);
  };
  useEffect(() => { load(); }, []);

  if (!status) return <div className="py-10">Loading…</div>;

  const shareUrl = `${window.location.origin}/register?ref=${status.code}`;
  const shareText = `Try Bula Golf with me — Fiji's WHS handicap + tee time app. Use my code ${status.code} and we both get 1 month of Bula Pro free.`;

  const copy = async (txt, label) => {
    try { await navigator.clipboard.writeText(txt); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed"); }
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Bula Golf", text: shareText, url: shareUrl }); } catch {}
    } else copy(`${shareText} ${shareUrl}`, "Link + message");
  };
  const redeem = async () => {
    if (!redeemCode.trim()) return;
    setRedeeming(true);
    try {
      const r = await api.post("/referrals/redeem", { code: redeemCode.trim() });
      toast.success(`+${r.data.days_added} days of Bula Pro! Thanks to ${r.data.referrer_name}.`);
      setRedeemCode("");
      await refresh?.();
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setRedeeming(false); }
  };

  return (
    <div className="space-y-7 max-w-3xl">
      <div className="rise">
        <span className="label-eyebrow flex items-center gap-1.5"><Gift size={12} weight="fill" /> Refer a mate</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Give a month, get a month.</h1>
        <p className="text-[#0F2537]/70 mt-2 max-w-2xl">
          Share your code with another golfer. When they redeem it, <b>you both get 1 free month of Bula Pro</b> —
          Shot Tracker, Club Bag, Shot Map, Strokes Gained, the lot.
        </p>
      </div>

      {/* Code card */}
      <section className="card-default p-6 md:p-8 rise rise-delay-1" data-testid="referral-code-card">
        <span className="label-eyebrow">Your code</span>
        <div className="mt-2 flex flex-col sm:flex-row sm:items-end gap-3">
          <div
            data-testid="referral-code-value"
            className="font-heading font-black text-5xl sm:text-6xl tracking-[0.1em] text-[#0F2537]"
          >{status.code}</div>
          <button
            onClick={() => copy(status.code, "Code")}
            data-testid="copy-code-btn"
            className="self-start inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#0F2537] text-white text-xs font-bold hover:bg-[#0F2537]/90 transition"
          >
            <Copy size={14} weight="bold" /> Copy code
          </button>
        </div>
        <div className="mt-5 flex items-center gap-2 p-3 rounded-xl bg-[#0F2537]/5 border border-dashed border-[#0F2537]/15">
          <input
            readOnly value={shareUrl}
            data-testid="referral-link"
            className="flex-1 bg-transparent text-sm font-mono text-[#0F2537] truncate outline-none"
          />
          <button
            onClick={() => copy(shareUrl, "Link")}
            data-testid="copy-link-btn"
            className="inline-flex items-center gap-1 text-xs font-bold text-[#E05D3A] hover:underline shrink-0"
          >
            <Copy size={12} weight="bold" /> Copy link
          </button>
        </div>
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
          <button
            onClick={nativeShare}
            data-testid="share-native-btn"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#E05D3A] text-white text-sm font-bold hover:brightness-110 transition"
          >
            <ShareNetwork size={14} weight="bold" /> Share
          </button>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
            target="_blank" rel="noreferrer"
            data-testid="share-whatsapp-btn"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-[#25D366] text-white text-sm font-bold hover:brightness-110 transition"
          >
            <WhatsappLogo size={14} weight="fill" /> WhatsApp
          </a>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
            target="_blank" rel="noreferrer"
            data-testid="share-x-btn"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-black text-white text-sm font-bold hover:bg-[#0F2537] transition"
          >
            <XLogo size={14} weight="fill" /> X
          </a>
          <a
            href={`mailto:?subject=${encodeURIComponent("Join me on Bula Golf")}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`}
            data-testid="share-email-btn"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl bg-white border border-[#E2E0D8] text-[#0F2537] text-sm font-bold hover:bg-black/5 transition"
          >
            <Envelope size={14} weight="fill" /> Email
          </a>
        </div>
      </section>

      {/* Stats row */}
      <section className="grid grid-cols-2 md:grid-cols-3 gap-4 rise rise-delay-2">
        <Tile testid="stat-referrals" label="Friends joined" value={status.referrals_count} icon={Users} />
        <Tile testid="stat-months" label="Free months earned" value={status.free_months_earned} icon={Crown} accent />
        <Tile testid="stat-pro-until" label="Your Pro expires" value={user?.is_pro && user?.pro_expires_at ? new Date(user.pro_expires_at).toLocaleDateString() : "—"} icon={CheckCircle} />
      </section>

      {/* Redeem card */}
      <section className="card-default p-6 md:p-7 rise rise-delay-3" data-testid="redeem-card">
        <span className="label-eyebrow">Got a code from a mate?</span>
        <h2 className="font-heading font-bold text-xl mt-1">Redeem a referral code</h2>
        {status.redeemed_code ? (
          <div className="mt-3 flex items-center gap-2 p-3 rounded-xl bg-[#1E5631]/10 border border-[#1E5631]/20" data-testid="already-redeemed">
            <CheckCircle size={18} weight="fill" className="text-[#1E5631] shrink-0" />
            <span className="text-sm font-bold text-[#1E5631]">You redeemed code <span className="font-mono">{status.redeemed_code}</span> — enjoy your Pro month!</span>
          </div>
        ) : (
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <input
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
              placeholder="ENTER CODE"
              data-testid="redeem-code-input"
              maxLength={12}
              className="flex-1 rounded-lg border border-[#E2E0D8] bg-white px-3 py-2.5 text-sm font-mono font-bold tracking-widest uppercase"
            />
            <button
              onClick={redeem} disabled={redeeming || !redeemCode.trim()}
              data-testid="redeem-btn"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-[#0F2537] text-white text-sm font-bold disabled:opacity-50 hover:bg-[#0F2537]/90 transition"
            >
              {redeeming ? "Redeeming…" : "Redeem"}
            </button>
          </div>
        )}
        <p className="mt-3 text-xs text-[#0F2537]/55">One redemption per account. Self-referral disabled.</p>
      </section>

      {/* History */}
      {history.length > 0 && (
        <section className="card-default p-6 md:p-7 rise rise-delay-3" data-testid="referral-history">
          <span className="label-eyebrow">Recent joins</span>
          <ul className="mt-3 divide-y divide-[#E2E0D8]">
            {history.map((h, i) => (
              <li key={i} className="py-2.5 flex items-center justify-between text-sm">
                <span className="font-bold">{h.name}</span>
                <span className="text-xs text-[#0F2537]/60">{new Date(h.created_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function Tile({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="card-default p-5" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Icon size={14} weight="fill" className={accent ? "text-[#E05D3A]" : "text-[#0F2537]/60"} />
        <span className="label-eyebrow">{label}</span>
      </div>
      <div className={`font-heading font-black text-3xl mt-2 ${accent ? "text-[#E05D3A]" : ""}`}>{value}</div>
    </div>
  );
}
